import axios from "axios";
import { toast } from "react-toastify";
import axiosMiddleware from "redux-axios-middleware";
import thunk from "redux-thunk";
import { createStore, applyMiddleware } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import history from "../history";
import {
  initialize,
  addTranslation,
  setActiveLanguage
} from "react-localize-redux";

import {
  loadingStart,
  loadingStop,
  setError,
  refreshToken,
  setAuthToken,
  setAuthError,
  setRefreshingToken,
  resetAllReducers,
  savePreferences
} from "./actions";

import reducers from "./reducers/index";
import languagePack from "../lang";
import subscriptionTranslations from "../lang/subscription-translations";
import organisationPreferencesTranslations from "../lang/organisation-preferences-translations";
import { loadState, saveState } from "./offline";
import { APP_CONFIG } from "../config";

import Auth, { AUTH_TOKEN_REFRESH_BEFORE } from "../auth";

const auth = new Auth();

const notify = msg => {
  toast.error(msg, {
    position: toast.POSITION.TOP_CENTER,
    hideProgressBar: true
  });
};

const checkTokenExpired = (store, config) => {
  let avoidUrls = [
    "login",
    "validatetoken",
    "resetpassword",
    "sendemail",
    "signup",
    "otp/send",
    "otp/verify"
  ];

  if (avoidUrls.indexOf(config.url.toLowerCase()) >= 0) {
    return false;
  }
  let expires = store.getState().authReducer.expires;
  let currentTime = new Date().getTime();
  if (currentTime > expires) {
    try {
      auth.logout();
      return null;
    } catch (err) {
      window.location.href = "/login";
      return null;
    }
  }

  let timeLeftToExpire = (expires - currentTime) / 1000;
  return timeLeftToExpire < AUTH_TOKEN_REFRESH_BEFORE;
};

function watchRefreshingToken() {
  return new Promise(resolve => {
    let trackRefreshCompletion = window.setInterval(() => {
      if (!store.getState().authReducer.token_refreshing) {
        window.clearInterval(trackRefreshCompletion);
        resolve();
      }
    }, 100);
  });
}

const REFRESH_AUTH_TOKEN_SUCCESS = "REFRESH_AUTH_TOKEN_SUCCESS";
const REFRESH_AUTH_TOKEN_FAIL = "REFRESH_AUTH_TOKEN_FAIL";
const axiosMiddlewareOptions = {
  interceptors: {
    // request: [
    //   (store, config) => {
    //     store.dispatch(loadingStart())
    //     let token = store.getState().authReducer.token
    //       if (token)
    //         config.headers['Authorization'] = 'Bearer ' + token
    //     return config
    //   }
    // ],
    request: [
      (store, config) => {
        let isTokenExpired = checkTokenExpired(store, config);

        if (isTokenExpired === null) {
          store.dispatch(resetAllReducers());
          return Promise.reject();
        }

        store.dispatch(loadingStart());
        if (isTokenExpired === true) {
          //Refresh Token
          if (!store.getState().authReducer.token_refreshing) {
            store.dispatch(setRefreshingToken(true));

            return store
              .dispatch(
                refreshToken({
                  refresh_token: store.getState().authReducer.refresh_token,
                  grant_type: "refresh_token"
                })
              )
              .then(response => {
                store.dispatch(setRefreshingToken(false));

                switch (response.type) {
                  case REFRESH_AUTH_TOKEN_SUCCESS:
                    if (response.payload.data.success) {
                      const {
                        access_token,
                        refresh_token
                      } = response.payload.data;
                      let expires = new Date(
                        response.payload.data[".expires"]
                      ).getTime();
                      store.dispatch(
                        setAuthToken({
                          access_token,
                          refresh_token,
                          expires
                        })
                      );
                      auth.handleAuthentication({
                        access_token,
                        refresh_token,
                        expires
                      });

                      let token = store.getState().authReducer.token;
                      if (token) {
                        config.headers["Authorization"] = "Bearer " + token;
                        return Promise.resolve(config);
                      }
                    } else {
                      store.dispatch(setAuthError(true));
                    }
                    break;
                  case REFRESH_AUTH_TOKEN_FAIL:
                    store.dispatch(loadingStop());
                    store.dispatch(setAuthError(true));
                    history.replace("/login");
                    break;
                }
              });
          } else {
            return watchRefreshingToken().then(() => {
              let token = store.getState().authReducer.token;
              if (token) {
                config.headers["Authorization"] = "Bearer " + token;
                return Promise.resolve(config);
              }
            });
          }
        } else {
          let token = store.getState().authReducer.token;
          if (token) config.headers["Authorization"] = "Bearer " + token;
          return config;
        }
      }
    ],
    response: [
      {
        success: ({ getState, dispatch, getSourceAction }, res) => {
          dispatch(loadingStop());
          return res;
        },
        error: ({ getState, dispatch, getSourceAction }, error) => {
          // Always release the global loader first. Error reporting below must not
          // leave the UI blocked if a malformed response causes another exception.
          dispatch(loadingStop());

          const response = error && error.response;
          const status = response ? response.status : null;
          const statusText = (response && response.statusText) ||
            (error && error.message) ||
            "Request failed";
          const sourceAction = getSourceAction ? getSourceAction() : null;
          const sourceRequest = sourceAction && sourceAction.payload
            ? sourceAction.payload.request
            : null;
          const sourceUrl = sourceRequest && sourceRequest.url
            ? sourceRequest.url
            : "";
          const suppressMetricsRunNotFoundToast = status === 404 &&
            sourceAction &&
            sourceAction.type === "FETCH_METRICS_RUN_REQUEST" &&
            /^metrics\/[^/]+$/.test(sourceUrl);

          if (auth.isAuthenticated()) {
            if (status != 400 && !suppressMetricsRunNotFoundToast) notify(statusText);
          }

          if (!suppressMetricsRunNotFoundToast) {
            dispatch(setError(statusText));
          }

          return Promise.reject(error);
        }
      }
    ]
  }
};

const client = axios.create({
  baseURL: APP_CONFIG.baseURL,
  responseType: "json"
});

const persistedState = loadState();

const store = createStore(
  reducers,
  persistedState,
  composeWithDevTools(
    applyMiddleware(
      thunk,
      // createLogger(),
      axiosMiddleware(client, axiosMiddlewareOptions)
    )
  )
);

store.subscribe(() => {
  saveState(store.getState());
});

const locales = languagePack.supportedLocales;

store.dispatch(initialize(locales));
store.dispatch(addTranslation(languagePack.packs));
store.dispatch(addTranslation(subscriptionTranslations));
store.dispatch(addTranslation(organisationPreferencesTranslations));

const language = localStorage.getItem("language");
if (language) {
  store.dispatch(setActiveLanguage(language));
} else {
  store.dispatch(setActiveLanguage("en-GB"));
}

//Load Test List Preferences from Local Storage
if (localStorage.getItem("tests_prefernces")) {
  let preferences = JSON.parse(localStorage.getItem("tests_prefernces"));
  store.dispatch(
    savePreferences(preferences.pager, preferences.columns, false)
  );
}
//

store.dispatch(loadingStop());
// Supported
// 'en-GB', 'en-US', 'zh-CN', 'de-DE'

export default store;
